import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Heart, Leaf, TreePine, Sparkles, Users, Sun, Droplets, Clock, 
  Target, ChevronRight, AlertTriangle, CheckCircle, Badge as BadgeIcon,
  Filter, Search, ChevronDown, User, Activity, Calendar, Share2,
  RefreshCw, BookOpen, Star, Award, HelpCircle, MessageCircle, Play, FileText,
  Printer, Download, Globe, Flower, Info, Upload, Lock,
  Moon, MapPin, Plus, Eye, Mic, MicOff, Send, Brain
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionRegistrationModal from '../components/SubscriptionRegistrationModal';
import { healingAPI } from '../services/api';
import AuthModal from '../components/AuthModal';
import SubscriptionGate from '../components/SubscriptionGate';

const HealingGrounds = () => {
  const { isAuthenticated, user } = useAuth();
  const { isPaidUser, hasAccess, hasSubscription, subscriptionTier } = useSubscription();
  const { toast } = useToast();
  const [ailmentInput, setAilmentInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [healingResponse, setHealingResponse] = useState(null);
  const [selectedTradition, setSelectedTradition] = useState('all');
  const [healingHistory, setHealingHistory] = useState([]);
  const [tribalRegions, setTribalRegions] = useState([]);
  const [loadingTribalRegions, setLoadingTribalRegions] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionRemedies, setRegionRemedies] = useState([]);
  const [loadingRegionDetails, setLoadingRegionDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('healing'); // 'healing' or 'tribal-remedies'
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState('seekers'); // Default to seekers for healing
  const recognitionRef = useRef(null);
  const tribalRemedyDetailsRef = useRef(null);
  const [aiExecutiveSummary, setAiExecutiveSummary] = useState(null);
  const [isGeneratingAiSummary, setIsGeneratingAiSummary] = useState(false);

  // Convert enhanced healing response to display format
  const convertEnhancedResponse = (enhancedData, originalSymptoms) => {
    return {
      ailment: originalSymptoms,
      timestamp: new Date(),
      id: enhancedData.session_id || `enhanced_${Date.now()}`,
      category: enhancedData.symptom_analysis?.category || 'general',
      traditions: enhancedData.symptom_analysis?.preferred_traditions || ['indigenous'],
      
      // Convert plant recommendations to herbal protocol format
      herbalProtocol: {
        primary: (enhancedData.recommended_plants || []).map(plant => ({
          name: plant.name || 'Traditional Plant',
          tradition: 'Indigenous Traditional',
          preparation: plant.preparation || 'Traditional preparation methods',
          dosage: plant.dosage || 'As recommended by traditional practices',
          benefits: plant.use || plant.benefits || 'Traditional healing benefits'
        })),
        supportive: []
      },
      
      // Convert ceremonial practices
      spiritualCleansing: {
        fasting: {
          type: "Traditional Healing Fast",
          duration: "3 days aligned with natural cycles",
          protocol: [
            "Day 1: Light foods and healing teas",
            "Day 2: Broths and traditional preparations", 
            "Day 3: Gentle return to normal diet",
            "Break fast with gratitude ceremony"
          ]
        },
        rituals: (enhancedData.ceremonial_practices || []).map(practice => ({
          name: practice.name || 'Traditional Ceremony',
          time: practice.time || 'As needed',
          steps: Array.isArray(practice.instructions) ? practice.instructions : [practice.instructions || practice.purpose || 'Follow traditional practices']
        }))
      },
      
      // Convert lifestyle guidance to daily routine
      dailyRoutine: [
        { time: '6:00 AM', activity: 'Morning gratitude and breathwork', duration: '15 min' },
        { time: '12:00 PM', activity: 'Midday plant medicine preparation', duration: '10 min' },
        { time: '6:00 PM', activity: 'Evening healing ritual', duration: '20 min' },
        { time: '9:00 PM', activity: 'Preparation for restful sleep', duration: '15 min' }
      ],
      
      // Moon cycle guidance
      moonCycle: {
        newMoon: "Set healing intentions and begin new practices",
        waxingMoon: "Build energy and strengthen healing routines",
        fullMoon: "Peak healing ceremonies and releasing rituals",
        waningMoon: "Gentle cleansing and releasing old patterns"
      },
      
      // Safety warnings
      warnings: enhancedData.safety_warnings || [
        "Consult healthcare providers before using plant medicines",
        "Some herbs may interact with medications",
        "Respect Indigenous cultural protocols",
        "Start with small amounts to test reactions"
      ],
      
      // Enhanced data for reference
      enhancedData: enhancedData
    };
  };

  // File upload handlers
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB limit
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          variant: "destructive",
          title: "File Too Large",
          description: `${file.name} is too large. Maximum size is 10MB.`,
        });
        return false;
      }
      return true;
    });
    
    setUploadedFiles(prev => [...prev, ...validFiles]);
    
    if (validFiles.length > 0) {
      toast({
        title: "Files Uploaded",
        description: `${validFiles.length} file(s) uploaded successfully.`,
      });
    }
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Load user's healing sessions on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadHealingSessions();
    }
    loadTribalRegions();
  }, [isAuthenticated]);

  const loadTribalRegions = async () => {
    console.log('Starting to load tribal regions...');
    setLoadingTribalRegions(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/tribal-healing-remedies/regions`;
      console.log('API URL:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const regions = await response.json();
        console.log('Loaded tribal regions:', regions.length, regions);
        setTribalRegions(regions);
      } else {
        console.error('Failed to load tribal regions. Status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error loading tribal regions:', error);
    } finally {
      setLoadingTribalRegions(false);
    }
  };

  const loadRegionRemedies = async (regionId) => {
    setLoadingRegionDetails(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tribal-healing-remedies/region/${regionId}/remedies`);
      if (response.ok) {
        const remedies = await response.json();
        setRegionRemedies(remedies);
      } else {
        console.warn('No specific remedies found for region:', regionId);
        setRegionRemedies([]); // Still allow the region to show
      }
    } catch (error) {
      console.error('Failed to load region remedies:', error);
      setRegionRemedies([]); // Still allow the region to show
    } finally {
      setLoadingRegionDetails(false);
    }
  };

  const loadHealingSessions = async () => {
    try {
      const sessions = await healingAPI.getUserSessions();
      // Ensure sessions is an array before calling slice
      if (Array.isArray(sessions)) {
        setHealingHistory(sessions.slice(0, 5)); // Keep last 5
      } else {
        setHealingHistory([]); // Default to empty array
      }
    } catch (error) {
      console.error('Failed to load healing sessions:', error);
      setHealingHistory([]); // Set to empty array on error
    }
  };

  const healingTraditions = [
    { id: 'all', name: 'All Traditions', icon: Users, color: 'bg-purple-100 text-purple-800' },
    { id: 'indigenous', name: 'Indigenous North American', icon: Leaf, color: 'bg-green-100 text-green-800' },
    { id: 'african', name: 'African Traditional', icon: Sun, color: 'bg-yellow-100 text-yellow-800' },
    { id: 'caribbean', name: 'Caribbean Herbal', icon: Droplets, color: 'bg-blue-100 text-blue-800' }
  ];

  const ailmentCategories = [
    {
      type: 'physical',
      icon: Heart,
      color: 'text-red-600',
      examples: ['Headaches', 'Joint pain', 'Digestive issues', 'Fatigue', 'Skin conditions']
    },
    {
      type: 'emotional',
      icon: Brain,
      color: 'text-blue-600', 
      examples: ['Anxiety', 'Depression', 'Grief', 'Stress', 'Insomnia']
    },
    {
      type: 'spiritual',
      icon: Sparkles,
      color: 'text-purple-600',
      examples: ['Spiritual blockage', 'Loss of purpose', 'Energy imbalance', 'Ancestral healing', 'Soul wounds']
    }
  ];

  const mockHealingResponse = {
    ailment: "chronic anxiety and insomnia",
    category: "emotional",
    traditions: ["indigenous", "african", "caribbean"],
    herbalProtocol: {
      primary: [
        { 
          name: "Passionflower (Passiflora incarnata)",
          tradition: "Indigenous North American",
          preparation: "Tea - 1 tsp dried herb in 8oz hot water, steep 10-15 minutes",
          dosage: "2-3 cups daily, last cup 1 hour before bed",
          benefits: "Calms nervous system, reduces anxiety, promotes restful sleep"
        },
        {
          name: "Kanna (Sceletium tortuosum)", 
          tradition: "African Traditional",
          preparation: "Fermented leaf tea or tincture",
          dosage: "1/4 tsp powder in warm water 2x daily",
          benefits: "Natural mood elevator, reduces stress and anxiety"
        },
        {
          name: "Soursop Leaves (Annona muricata)",
          tradition: "Caribbean Herbal", 
          preparation: "Fresh or dried leaf tea",
          dosage: "1 cup before evening meal",
          benefits: "Deep relaxation, spiritual cleansing, peaceful sleep"
        }
      ],
      supportive: [
        { name: "Lavender", use: "Essential oil for pillow or bath" },
        { name: "Chamomile", use: "Evening tea blend with primary herbs" },
        { name: "Lemon Balm", use: "Fresh leaves in water throughout day" }
      ]
    },
    spiritualCleansing: {
      fasting: {
        type: "Gentle Moon Fast",
        duration: "3 days during new moon",
        protocol: [
          "Day 1: Fruit and herbal teas only",
          "Day 2: Vegetable broths and sacred teas", 
          "Day 3: Light vegetables and grains",
          "Break fast at sunrise with gratitude ceremony"
        ]
      },
      rituals: [
        {
          name: "Morning Grounding",
          time: "Sunrise",
          steps: [
            "Stand barefoot on earth for 10 minutes",
            "Breathe deeply, visualize roots growing from feet",
            "Speak gratitude to four directions",
            "Drink sacred tea while setting daily intention"
          ]
        },
        {
          name: "Evening Release",
          time: "Sunset",
          steps: [
            "Light white candle and sage or palo santo",
            "Write anxieties on paper, burn safely",
            "Take warm bath with Epsom salt and lavender",
            "Meditate on ancestral protection before sleep"
          ]
        }
      ]
    },
    dailyRoutine: [
      { time: "6:00 AM", activity: "Morning grounding ritual", duration: "15 min" },
      { time: "8:00 AM", activity: "Passionflower tea with breakfast", duration: "5 min" },
      { time: "12:00 PM", activity: "Kanna preparation with lunch", duration: "5 min" },
      { time: "6:00 PM", activity: "Soursop leaf tea before dinner", duration: "10 min" },
      { time: "8:00 PM", activity: "Evening release ritual", duration: "20 min" },
      { time: "9:30 PM", activity: "Final herbal tea blend", duration: "5 min" }
    ],
    moonCycle: {
      newMoon: "Begin cleansing fast, set new intentions",
      waxingMoon: "Increase herbal dosages, build energy",
      fullMoon: "Peak spiritual practices, moon water creation", 
      waningMoon: "Release ceremonies, reduce stimulating herbs"
    },
    warnings: [
      "Consult healthcare provider before starting any herbal protocol",
      "Start with small doses to test individual reactions",
      "Pregnant/nursing mothers should avoid certain herbs",
      "Stop use if adverse reactions occur"
    ]
  };

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setAilmentInput(prev => prev + (prev ? ' ' : '') + transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const startListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const analyzeAilment = async () => {
    if (!ailmentInput.trim()) return;

    if (!isAuthenticated) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please log in to access healing wisdom.",
      });
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // Map selected tradition to API format
      let traditions = [];
      if (selectedTradition === 'all' || selectedTradition === 'indigenous') {
        traditions.push('native_american');
      }
      if (selectedTradition === 'all' || selectedTradition === 'african') {
        traditions.push('african');
      }
      if (selectedTradition === 'all' || selectedTradition === 'caribbean') {
        traditions.push('caribbean');
      }
      if (traditions.length === 0) {
        traditions = ['native_american', 'african', 'caribbean'];
      }

      // Check if we have uploaded files
      if (uploadedFiles.length > 0) {
        // Use enhanced healing with file upload
        const formData = new FormData();
        formData.append('symptoms', ailmentInput);
        formData.append('preferred_traditions', JSON.stringify(traditions));
        
        // Add uploaded files
        uploadedFiles.forEach((file, index) => {
          formData.append('files', file);
        });

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enhanced-healing/analyze-with-files`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Enhanced analysis failed: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
          // Convert enhanced healing response to display format
          const enhancedResponse = convertEnhancedResponse(result.healing_guidance, ailmentInput);
          setHealingResponse(enhancedResponse);
          setHealingHistory(prev => [enhancedResponse, ...prev.slice(0, 4)]);
          
          // Generate AI Executive Summary with plant remedy context
          if (result.healing_guidance.recommended_plants && result.healing_guidance.recommended_plants.length > 0) {
            generateAiExecutiveSummary(ailmentInput, result.healing_guidance.recommended_plants);
          } else {
            generateAiExecutiveSummary(ailmentInput, []);
          }
          
          toast({
            title: "Enhanced Healing Analysis Complete!",
            description: `Analyzed with ${uploadedFiles.length} uploaded files and tribal healing archive.`,
          });
        } else {
          throw new Error(result.error || 'Enhanced analysis failed');
        }
      } else {
        // Use enhanced healing without files
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enhanced-healing/analyze-symptoms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            symptoms: ailmentInput,
            preferred_traditions: traditions
          }),
        });

        if (!response.ok) {
          throw new Error(`Enhanced analysis failed: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
          // Convert enhanced healing response to display format
          const enhancedResponse = convertEnhancedResponse(result, ailmentInput);
          setHealingResponse(enhancedResponse);
          setHealingHistory(prev => [enhancedResponse, ...prev.slice(0, 4)]);
          
          // Generate AI Executive Summary with plant remedy context
          if (result.recommended_plants && result.recommended_plants.length > 0) {
            generateAiExecutiveSummary(ailmentInput, result.recommended_plants);
          } else {
            generateAiExecutiveSummary(ailmentInput, []);
          }
          
          toast({
            title: "🌿 Enhanced Healing Analysis Complete!",
            description: "Received personalized guidance from our Tribal Healing Archive.",
          });
        } else {
          throw new Error(result.error || 'Enhanced analysis failed');
        }
      }

      setIsAnalyzing(false);

    } catch (error) {
      console.error('Enhanced healing analysis error:', error);
      setIsAnalyzing(false);
      
      // Fallback to original healing API if enhanced fails
      try {
        console.log('Falling back to original healing API...');
        
        // Determine category based on ailment input
        let category = 'physical'; // default
        if (ailmentInput.toLowerCase().includes('anxie') || ailmentInput.toLowerCase().includes('stress') || 
            ailmentInput.toLowerCase().includes('depress') || ailmentInput.toLowerCase().includes('emotion')) {
          category = 'emotional';
        } else if (ailmentInput.toLowerCase().includes('spirit') || ailmentInput.toLowerCase().includes('soul') ||
                   ailmentInput.toLowerCase().includes('energy') || ailmentInput.toLowerCase().includes('purpose')) {
          category = 'spiritual';
        }

        // Map selected tradition to API format
        let traditions = [];
        if (selectedTradition === 'all' || selectedTradition === 'indigenous') {
          traditions.push('native_american');
        }
        if (selectedTradition === 'all' || selectedTradition === 'african') {
          traditions.push('african');
        }
        if (selectedTradition === 'all' || selectedTradition === 'caribbean') {
          traditions.push('caribbean');
        }
        if (traditions.length === 0) {
          traditions = ['native_american', 'african', 'caribbean'];
        }

        // Call the original API as fallback
        const result = await healingAPI.seekWisdom({
          ailment_description: ailmentInput,
          category: category,
          preferred_traditions: traditions,
          severity: 'moderate',
          current_treatments: null,
          allergies_contraindications: null
        });

        // Start polling for completion
        pollForHealingCompletion(result.id);

        toast({
          title: "Healing Wisdom Session Started",
          description: "Your personalized healing protocols are being prepared.",
        });

      } catch (fallbackError) {
        console.error('Fallback healing analysis also failed:', fallbackError);
        toast({
          variant: "destructive",
          title: "Analysis Failed",
          description: "Both enhanced and traditional healing analysis failed. Please try again later.",
        });
      }
    }
  };


  // Generate AI Executive Summary with plant remedy context
  const generateAiExecutiveSummary = async (symptoms, plantRemedies = []) => {
    setIsGeneratingAiSummary(true);
    
    try {
      // Set a longer timeout for AI generation (90 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plant-remedies/generate-executive-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          symptoms: symptoms,
          remedy_context: plantRemedies
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`AI Summary generation failed: ${response.status}`);
      }

      const result = await response.json();
      setAiExecutiveSummary(result.summary);
      
      // Check if this is a fallback response
      const isFallback = result.summary && result.summary.includes('temporarily unable to access');
      
      toast({
        title: isFallback ? "⚠️ Healing Wisdom (Fallback)" : "🌿 Ancestral Wisdom Received",
        description: isFallback 
          ? "Providing general healing guidance. The full wisdom system will reconnect shortly."
          : "The Healing Wisdom has spoken with guidance for your journey.",
      });
      
    } catch (error) {
      console.error('Error generating AI executive summary:', error);
      
      // Provide more specific error messages
      let errorMessage = "Unable to generate healing summary at this time.";
      if (error.name === 'AbortError') {
        errorMessage = "Request timed out. The healing wisdom is taking longer than expected. Your plant remedies are still available below.";
      }
      
      toast({
        variant: "destructive",
        title: "Could Not Reach Ancestral Wisdom",
        description: errorMessage,
      });
      
      // Even if AI summary fails, we still have the plant remedies
      console.log('Plant remedies are still available for the user');
      
    } finally {
      setIsGeneratingAiSummary(false);
    }
  };


  // Download Healing Guidance as PDF
  const downloadHealingPdf = async () => {
    if (!healingResponse) {
      toast({
        variant: "destructive",
        title: "No Healing Guidance",
        description: "Please seek healing wisdom first before downloading PDF.",
      });
      return;
    }

    try {
      toast({
        title: "Preparing Your PDF...",
        description: "Creating a beautiful document of your healing guidance.",
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plant-remedies/generate-healing-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          healing_data: healingResponse,
          ai_summary: aiExecutiveSummary || ''
        }),
      });

      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.status}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const ailment = healingResponse.ailment || 'Healing_Guidance';
      const safeAilment = ailment.replace(/[^a-zA-Z0-9 -]/g, '').replace(/\s+/g, '_').substring(0, 50);
      link.download = `TribalRoot_Healing_Guidance_${safeAilment}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "✅ PDF Downloaded!",
        description: "Your healing guidance has been saved successfully.",
      });
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: "Unable to generate PDF at this time. Please try again.",
      });
    }
  };



  const pollForHealingCompletion = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 15; // 15 seconds max
    
    const poll = async () => {
      try {
        const session = await healingAPI.getSession(sessionId);
        
        if (session.status === 'completed') {
          // Convert backend format to frontend display format
          console.log('Converting backend session data:', session);
          console.log('Session protocols:', session.protocols);
          
          // Extract herbal protocols - be more flexible with remedy types
          const herbalProtocols = session.protocols.filter(p => 
            p.remedy_type === 'herbal' || 
            p.remedy_type === 'HERBAL' ||
            (p.ingredients && p.ingredients.length > 0) // Any protocol with ingredients is likely herbal
          );
          const ritualProtocols = session.protocols.filter(p => 
            p.remedy_type === 'ritual' || 
            p.remedy_type === 'ceremony' ||
            p.remedy_type === 'RITUAL' ||
            p.remedy_type === 'CEREMONY'
          );
          const lifestyleProtocols = session.protocols.filter(p => 
            p.remedy_type === 'lifestyle' ||
            p.remedy_type === 'LIFESTYLE' ||
            p.remedy_type === 'dietary' ||
            p.remedy_type === 'DIETARY'
          );
          
          console.log('Filtered protocols:', {
            herbal: herbalProtocols.length,
            ritual: ritualProtocols.length,
            lifestyle: lifestyleProtocols.length
          });
          
          const response = {
            ailment: ailmentInput,
            timestamp: new Date(),
            id: session.id,
            category: session.category,
            traditions: session.preferred_traditions,
            // Convert backend protocols to frontend expected format
            herbalProtocol: {
              primary: (herbalProtocols.length > 0 ? herbalProtocols : session.protocols.slice(0, 3)).map(protocol => {
                // Get the first ingredient for the name, or use protocol name as fallback
                const primaryIngredient = protocol.ingredients && protocol.ingredients.length > 0 
                  ? protocol.ingredients[0].name 
                  : protocol.name;
                
                // Format tradition name for display
                const traditionName = protocol.tradition === 'native_american' ? 'Native American' : 
                                    protocol.tradition === 'african' ? 'African Traditional' :
                                    protocol.tradition === 'caribbean' ? 'Caribbean Herbal' : 
                                    protocol.tradition === 'NATIVE_AMERICAN' ? 'Native American' :
                                    protocol.tradition === 'AFRICAN' ? 'African Traditional' :
                                    protocol.tradition === 'CARIBBEAN' ? 'Caribbean Herbal' :
                                    protocol.tradition.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                
                return {
                  name: primaryIngredient || protocol.name || 'Traditional Remedy',
                  tradition: traditionName,
                  preparation: protocol.preparation_steps && protocol.preparation_steps.length > 0 
                    ? protocol.preparation_steps.join('. ') 
                    : 'Follow traditional preparation methods',
                  dosage: protocol.usage_instructions && protocol.usage_instructions.length > 0
                    ? protocol.usage_instructions.join('. ')
                    : 'As directed by traditional practices',
                  benefits: protocol.cultural_context || protocol.spiritual_significance || 'Traditional healing benefits'
                };
              }),
              supportive: lifestyleProtocols.map(protocol => ({
                name: protocol.name,
                use: protocol.cultural_context || protocol.usage_instructions?.join('. ') || 'Traditional use'
              }))
            },
            spiritualCleansing: {
              fasting: {
                type: "Gentle Healing Fast",
                duration: "3 days during new moon",
                protocol: [
                  "Day 1: Light foods and herbal teas",
                  "Day 2: Broths and healing teas", 
                  "Day 3: Gentle return to normal diet",
                  "Break fast with gratitude ceremony"
                ]
              },
              rituals: ritualProtocols.map(protocol => ({
                name: protocol.name,
                time: protocol.duration || 'As needed',
                steps: protocol.preparation_steps && protocol.preparation_steps.length > 0 
                  ? protocol.preparation_steps 
                  : [protocol.cultural_context || 'Follow traditional practices']
              }))
            },
            dailyRoutine: lifestyleProtocols.slice(0, 4).map((protocol, index) => {
              const times = ['6:00 AM', '12:00 PM', '6:00 PM', '9:00 PM'];
              return {
                time: times[index] || '12:00 PM',
                activity: protocol.name,
                duration: protocol.duration || '10 min'
              };
            }),
            moonCycle: {
              newMoon: "Focus on new beginnings and setting healing intentions",
              waxingMoon: "Build energy and strengthen healing practices",
              fullMoon: "Peak healing power, perfect for ceremony and release",
              waningMoon: "Release what no longer serves, gentle cleansing"
            },
            warnings: session.protocols.flatMap(p => p.contraindications || []).slice(0, 3).concat([
              "Consult healthcare provider before use",
              "Stop use if adverse reactions occur",
              "Not recommended during pregnancy without guidance"
            ]).slice(0, 5)
          };
          
          console.log('Converted response for frontend:', response);
          
          setHealingResponse(response);
          setHealingHistory(prev => [response, ...prev.slice(0, 4)]); // Keep last 5
          setIsAnalyzing(false);
          
          toast({
            title: "Healing Protocols Ready!",
            description: `Generated ${session.protocols.length} personalized healing protocols.`,
          });
          
        } else if (session.status === 'failed') {
          setIsAnalyzing(false);
          toast({
            variant: "destructive",
            title: "Analysis Failed",
            description: "Healing analysis failed. Please try again.",
          });
          
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000); // Poll every second
        } else {
          setIsAnalyzing(false);
          toast({
            variant: "destructive",
            title: "Analysis Timeout",
            description: "Healing analysis is taking longer than expected. Please try again.",
          });
        }
      } catch (error) {
        setIsAnalyzing(false);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to check healing session status. Please try again.",
        });
      }
    };
    
    poll();
  };

  // Helper functions for tribal healing processes
  const getPreparationMethods = (tribeName) => {
    const methods = {
      'Abenaki (W8banaki)': [
        'Boil bark and leaves into healing teas',
        'Create poultices from fresh plant material',
        'Steam inhalation for respiratory issues',
        'Smudging with sacred plants'
      ],
      'Sharakhi (Cherokee)': [
        'Emergency burn treatments with fresh leaves',
        'Steep inner bark for migraine relief',
        'Crush fresh stems for topical application',
        'Ferment plant material for long-term use'
      ],
      'Sioux (Dakota/Lakota)': [
        'Chew fresh roots for immediate relief',
        'Create steam baths with aromatic plants',
        'Mix herbs with buffalo fat for salves',
        'Brew strong teas for internal medicine'
      ],
      'Kānaka Maoli (Native Hawaiian)': [
        'Ferment fruits into healing juices',
        'Warm leaves for healing poultices',
        'Extract oils from nuts and seeds',
        'Prepare ceremonial drinks with prayers'
      ],
      'Inuit': [
        'Brew hardy Arctic plants into teas',
        'Use moss and cotton grass for wound care',
        'Create smoking blends for respiratory health',
        'Prepare vitamin-rich plant foods'
      ]
    };
    
    return methods[tribeName] || [
      'Traditional plant preparation methods',
      'Careful harvesting with respect for nature',
      'Sacred ceremonies accompany medicine making',
      'Knowledge passed through generations'
    ];
  };

  const getSpiritualPractices = (tribeName) => {
    const practices = {
      'Abenaki (W8banaki)': [
        'Four sacred medicines ceremony (tobacco, cedar, sage, sweetgrass)',
        'Offerings made before plant gathering',
        'Honor songs during healing rituals',
        'Balance between physical and spiritual healing'
      ],
      'Sharakhi (Cherokee)': [
        'Prayer and thanksgiving with plant harvest',
        'Recognition of each plant\'s sacred value',
        'Healing ceremonies combine herbs with prayer',
        'Balance and harmony in all treatments'
      ],
      'Sioux (Dakota/Lakota)': [
        'Chanunpa (sacred pipe) ceremonies',
        'Four Great Medicines rituals',
        'Sweat lodge healing with plant smoke',
        'Vision quests and plant spirit communication'
      ],
      'Kānaka Maoli (Native Hawaiian)': [
        'Pule (prayers) during plant gathering',
        'Ho\'oponopono healing ceremonies',
        'Offerings to plant spirits and ancestors',
        'Lā\'au lapa\'au traditional protocols'
      ],
      'Inuit': [
        'Angakkuq (shaman) healing ceremonies',
        'Throat singing for spiritual healing',
        'Ice and snow purification rituals',
        'Aurora borealis spiritual connection'
      ]
    };
    
    return practices[tribeName] || [
      'Sacred plant gathering ceremonies',
      'Prayers and offerings to plant spirits',
      'Spiritual cleansing before healing',
      'Connection to ancestral healing wisdom'
    ];
  };

  // Render function for the main healing wisdom content
  const renderHealingWisdomContent = () => (
    <>
      {/* Input Section */}
      <div className="lg:col-span-2 space-y-6">
        {/* Healing Ground Symptom Intake */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">🌿 Healing Ground Symptom Intake</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Welcome Message */}
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-green-800 mb-2">Welcome to the Healing Grounds.</h3>
              <p className="text-gray-700 mb-4">
                Tell me how you're feeling. What symptoms are you experiencing—physically, emotionally, or spiritually?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                You can describe it in your own words (e.g., "sore knees and dry mouth") or use complete sentences 
                (e.g., "I've had trouble sleeping and my stomach feels bloated").
              </p>
            </div>

            {/* What You'll Receive */}
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-3">Once we receive your symptoms, we'll search our Tribal Healing Archive and share with you:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">🌿</span>
                  <span className="text-sm font-medium text-gray-700">Natural, Indigenous Remedies</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">🪶</span>
                  <span className="text-sm font-medium text-gray-700">Herbal & Spiritual Plant Uses</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">🔥</span>
                  <span className="text-sm font-medium text-gray-700">Ceremonial Healing Practices</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">🧘🏾‍♂️</span>
                  <span className="text-sm font-medium text-gray-700">Diet, Movement, and Meditation Guidance</span>
                </div>
              </div>
            </div>

            {/* Symptom Categories for Reference */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {ailmentCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <Card key={category.type} className="p-4 hover:shadow-md transition-shadow">
                    <div className="text-center">
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${category.color}`} />
                      <h4 className="font-semibold capitalize mb-2">{category.type}</h4>
                      <div className="text-xs text-gray-600 space-y-1">
                        {category.examples.map((example, index) => (
                          <div key={index} className="p-1 bg-gray-50 rounded">{example}</div>
                        ))}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Input Area */}
            <div className="space-y-4">
              <Textarea
                placeholder="Tell me about your symptoms... For example: 'sore knees and dry mouth' or 'I've had trouble sleeping and my stomach feels bloated'"
                value={ailmentInput}
                onChange={(e) => setAilmentInput(e.target.value)}
                rows={4}
                className="w-full"
              />

              {/* Document Upload Section */}
              <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300">
                <h5 className="font-medium text-gray-700 mb-2 flex items-center">
                  <Upload className="w-4 h-4 mr-2" />
                  Optional: Upload Medical or Herbal Intake Forms
                </h5>
                <p className="text-sm text-gray-600 mb-3">
                  Upload any past medical reports, herbal intake forms, or health documents to help us provide more personalized healing guidance.
                </p>
                <input
                  type="file"
                  id="medical-upload"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <label
                  htmlFor="medical-upload"
                  className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Files
                </label>
                {uploadedFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</p>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                        <span className="text-sm text-gray-600">{file.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUploadedFile(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={isListening ? stopListening : startListening}
                  className={`flex-1 text-sm ${isListening ? 'bg-red-50 border-red-300' : ''}`}
                >
                  {isListening ? (
                    <>
                      <MicOff className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="hidden xs:inline">Stop </span>Listening
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-1 sm:mr-2" />
                      <span className="hidden xs:inline">Speak Your </span>Ailment
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={analyzeAilment}
                  disabled={!ailmentInput.trim() || isAnalyzing}
                  className="flex-1 bg-green-700 hover:bg-green-800 text-white text-sm"
                >
                  {isAnalyzing ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                      Consulting<span className="hidden xs:inline"> Ancestors</span>...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-1 sm:mr-2" />
                      Seek <span className="hidden xs:inline">Healing </span>Wisdom
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Healing Response */}
        {healingResponse && (
          <div className="space-y-6">
            {/* Header */}
            <Card className="bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">🌿 Healing Guidance</h3>
                    <p className="text-green-100 text-sm sm:text-base">
                      For: <span className="font-semibold">{healingResponse.ailment}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-white border-white hover:bg-white hover:text-green-600 text-xs sm:text-sm flex-1 sm:flex-none"
                      onClick={printHealingCard}
                    >
                      <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Print
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="text-white border-white hover:bg-white hover:text-green-600 text-xs sm:text-sm flex-1 sm:flex-none"
                      onClick={downloadHealingPdf}
                    >
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>


            {/* AI Executive Summary - Loading State */}
            {isGeneratingAiSummary && (
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    <div className="text-center">
                      <h4 className="font-semibold text-purple-900 mb-1">Consulting Ancestral Wisdom...</h4>
                      <p className="text-sm text-purple-700">The Healing Wisdom is preparing your guidance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Executive Summary - Display */}
            {aiExecutiveSummary && !isGeneratingAiSummary && (
              <Card className="bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border-2 border-amber-300 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-amber-100 to-orange-100 border-b-2 border-amber-300">
                  <CardTitle className="flex items-center text-2xl text-amber-900">
                    <Sparkles className="w-6 h-6 mr-3 text-amber-600" />
                    Executive Summary: Healing Wisdom Speaks
                  </CardTitle>
                  <p className="text-sm text-amber-700 mt-2 italic">
                    From the ancestral voice of TribalRoot.AI, speaking through organic remedies, rituals, and sacred traditions
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose prose-lg max-w-none">
                    <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                      {aiExecutiveSummary.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className="mb-4 text-base">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-amber-200">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                          <Leaf className="w-5 h-5 text-amber-700" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-amber-800 font-medium mb-1">
                          🌿 Guided by Ancestral Wisdom
                        </p>
                        <p className="text-xs text-amber-700">
                          This guidance blends traditional Indigenous, African, and Caribbean healing knowledge 
                          with the specific plant remedies from our tribal healing archive. Below you'll find 
                          detailed herbal protocols, spiritual practices, and daily routines to support your healing journey.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}


            {/* Herbal Protocol */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Leaf className="w-5 h-5 mr-2 text-green-600" />
                  Primary Herbal Protocol
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {healingResponse.herbalProtocol && healingResponse.herbalProtocol.primary ? (
                  healingResponse.herbalProtocol.primary.map((herb, index) => (
                    <div key={index} className="border-l-4 border-green-500 pl-4 py-3 bg-green-50">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-lg">{herb.name || 'Unknown Herb'}</h4>
                        <Badge className="bg-green-600">{herb.tradition || 'Traditional'}</Badge>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p><strong>Preparation:</strong> {herb.preparation || 'Not specified'}</p>
                        <p><strong>Dosage:</strong> {herb.dosage || 'Consult practitioner'}</p>
                        <p><strong>Benefits:</strong> {herb.benefits || 'Traditional wellness support'}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    No herbal protocol available for this session.
                  </p>
                )}
                
                {healingResponse.herbalProtocol && healingResponse.herbalProtocol.supportive && healingResponse.herbalProtocol.supportive.length > 0 && (
                  <div className="pt-4">
                    <h5 className="font-semibold mb-2">Supportive Herbs:</h5>
                    <div className="flex flex-wrap gap-2">
                      {healingResponse.herbalProtocol.supportive.map((herb, index) => (
                        <Badge key={index} variant="outline" className="p-2">
                          <strong>{herb.name || 'Herb'}:</strong> {herb.use || 'Traditional use'}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Spiritual Cleansing */}
            {healingResponse?.spiritualCleansing && (
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                    Spiritual Cleansing & Rituals
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Fasting Protocol */}
                  {healingResponse.spiritualCleansing?.fasting && (
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-purple-800 mb-3">
                        {healingResponse.spiritualCleansing.fasting.type}
                      </h4>
                      <p className="text-sm text-purple-700 mb-3">
                        Duration: {healingResponse.spiritualCleansing.fasting.duration}
                      </p>
                      <div className="space-y-2">
                        {healingResponse.spiritualCleansing.fasting.protocol?.map((step, index) => (
                          <div key={index} className="flex items-start">
                            <Star className="w-4 h-4 mr-2 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{step}</span>
                          </div>
                        )) || []}
                      </div>
                    </div>
                  )}

                  {/* Daily Rituals */}
                  {healingResponse.spiritualCleansing?.rituals && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {healingResponse.spiritualCleansing.rituals.map((ritual, index) => (
                        <div key={index} className="bg-blue-50 p-4 rounded-lg">
                          <div className="flex items-center mb-3">
                            {ritual.time === 'Sunrise' ? (
                              <Sun className="w-5 h-5 mr-2 text-yellow-600" />
                            ) : (
                              <Moon className="w-5 h-5 mr-2 text-blue-600" />
                            )}
                            <h4 className="font-semibold">{ritual.name}</h4>
                          </div>
                          <div className="space-y-2">
                            {ritual.steps?.map((step, stepIndex) => (
                              <div key={stepIndex} className="flex items-start">
                                <div className="w-4 h-4 rounded-full bg-blue-300 mr-2 mt-1 flex-shrink-0 text-xs flex items-center justify-center text-white font-bold">
                                  {stepIndex + 1}
                                </div>
                                <span className="text-sm">{step}</span>
                              </div>
                            )) || []}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Daily Routine Card */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                  Daily Healing Routine
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {healingResponse.dailyRoutine?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-3 text-orange-600" />
                        <span className="font-medium">{item.time}</span>
                      </div>
                      <span className="text-sm text-gray-700 flex-1 mx-4">{item.activity}</span>
                      <Badge variant="outline">{item.duration}</Badge>
                    </div>
                  )) || (
                    <div className="text-gray-500 text-center py-4">No daily routine available</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Moon Cycle Guidance */}
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Moon className="w-5 h-5 mr-2 text-indigo-600" />
                  Moon Cycle Healing Guidance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {healingResponse.moonCycle ? Object.entries(healingResponse.moonCycle).map(([phase, guidance]) => (
                    <div key={phase} className="p-3 bg-white rounded-lg">
                      <h5 className="font-semibold capitalize mb-2 text-indigo-800">
                        {phase.replace(/([A-Z])/g, ' $1').trim()}
                      </h5>
                      <p className="text-sm text-gray-700">{guidance}</p>
                    </div>
                  )) : (
                    <div className="text-gray-500 text-center py-4 col-span-2">No moon cycle guidance available</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Warnings */}
            <Card className="bg-yellow-50 border-l-4 border-yellow-400 shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-white font-bold text-sm">!</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-2">Important Safety Guidelines</h4>
                    <ul className="space-y-1">
                      {healingResponse.warnings?.map((warning, index) => (
                        <li key={index} className="text-sm text-yellow-700">• {warning}</li>
                      )) || (
                        <li className="text-sm text-yellow-700">• Consult healthcare provider before use</li>
                      )}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-1">
        <div className="sticky top-4 space-y-6">
          {/* Quick Healing Options */}
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Quick Healing Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                "Stress and overwhelm",
                "Chronic fatigue",
                "Digestive issues", 
                "Sleep problems",
                "Emotional healing",
                "Spiritual cleansing"
              ].map((ailment, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left h-auto p-3"
                  onClick={() => setAilmentInput(ailment)}
                >
                  <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-sm">{ailment}</span>
                </Button>
              ))}
            </CardContent>
          </Card>

          {/* Healing History */}
          {healingHistory.length > 0 && (
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Recent Healing Sessions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {healingHistory && healingHistory.length > 0 ? (
                  healingHistory.map((session) => (
                    <div key={session.id || Math.random()} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {session.ailment ? session.ailment.slice(0, 40) + '...' : 'Healing session'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {session.timestamp ? 
                          (session.timestamp instanceof Date ? 
                            session.timestamp.toLocaleDateString() : 
                            new Date(session.timestamp).toLocaleDateString()
                          ) : 
                          'Recent session'
                        }
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 p-1 h-auto"
                        onClick={() => setHealingResponse(session)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View Again
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No healing sessions yet. Start your first session above.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Healing Resources */}
          <Card className="bg-gradient-to-br from-green-50 to-blue-50 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <BookOpen className="w-5 h-5 mr-2" />
                Sacred Healing Wisdom
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 leading-relaxed">
                Our healing protocols draw from thousands of years of ancestral wisdom, 
                combining traditional Indigenous, African, and Caribbean healing practices 
                with modern understanding of plant medicine and spiritual wellness.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );

  // Render function for tribal remedies content - ALWAYS ACCESSIBLE NOW
  const renderTribalRemediesContent = () => {
    // Always show tribal remedies content (no subscription gate)
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Indigenous Healing Remedies by Tribal Region
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore traditional healing knowledge from 24 Indigenous tribal regions across the Americas, 
            featuring ancestral plant medicine, preparation methods, and cultural wisdom passed down through generations.
          </p>
        </div>

        {/* Regions Grid */}
        {loadingTribalRegions ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <span className="ml-4 text-lg text-gray-600">Loading tribal healing regions...</span>
          </div>
        ) : tribalRegions.length === 0 ? (
          <div className="text-center py-12">
            <TreePine className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-4">No Tribal Regions Found</h3>
            <p className="text-gray-600">
              We're working to load the tribal healing regions. Please try refreshing the page.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tribalRegions.map(region => (
              <Card key={region.region} className="group hover:shadow-lg transition-all duration-300 cursor-pointer"
                    onClick={async () => {
                      setSelectedRegion(region);
                      // Load detailed remedies for this region
                      await loadRegionRemedies(region.region);
                      document.getElementById('region-details')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mr-4">
                      <TreePine className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 group-hover:text-green-600 transition-colors">
                        {region.tribe_name}
                      </h3>
                      <p className="text-sm text-gray-500">{region.total_remedies} healing remedies</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {region.cultural_overview}
                  </p>
                  
                  <div className="mt-4 flex flex-wrap gap-1">
                    {region.healing_plants?.slice(0, 3).map((plant, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {plant}
                      </Badge>
                    ))}
                    {region.healing_plants?.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{region.healing_plants.length - 3} more
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Selected Region Details */}
        {selectedRegion && (
          <div id="region-details" className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mr-6">
                  <TreePine className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{selectedRegion.tribe_name}</h2>
                  <p className="text-lg text-green-700">{selectedRegion.total_remedies} Traditional Healing Remedies</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Cultural Overview</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedRegion.cultural_overview}</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Sacred Healing Plants</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedRegion.healing_plants?.map((plant, idx) => (
                      <Badge key={idx} className="bg-green-200 text-green-800 text-sm py-1 px-3">
                        {plant}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Detailed Remedies */}
              {loadingRegionDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  <span className="ml-3 text-gray-600">Loading detailed remedies...</span>
                </div>
              ) : regionRemedies && regionRemedies.length > 0 ? (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Sacred Healing Remedies & Plants</h3>
                  <div className="space-y-6">
                    {regionRemedies.map((remedy, idx) => (
                      <Card key={idx} className="bg-white border-l-4 border-l-green-500">
                        <CardContent className="p-6">
                          {/* Remedy Header */}
                          <div className="mb-4">
                            <h4 className="text-xl font-bold text-green-900 mb-2">{remedy.remedy_name}</h4>
                            <div className="flex flex-wrap gap-2 mb-3">
                              <Badge className="bg-green-100 text-green-800">{remedy.remedy_category}</Badge>
                              {remedy.primary_uses.slice(0, 3).map((use, useIdx) => (
                                <Badge key={useIdx} variant="outline" className="text-xs">{use}</Badge>
                              ))}
                            </div>
                          </div>

                          {/* Sacred Healing Plants */}
                          {remedy.healing_plants && remedy.healing_plants.length > 0 && (
                            <div className="mb-4">
                              <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <Leaf className="w-4 h-4 mr-2 text-green-600" />
                                Sacred Healing Plants
                              </h5>
                              <div className="space-y-3">
                                {remedy.healing_plants.map((plant, plantIdx) => (
                                  <div key={plantIdx} className="bg-green-50 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <h6 className="font-semibold text-green-900">{plant.common_name}</h6>
                                        {plant.scientific_name && (
                                          <p className="text-sm italic text-gray-600 mb-1">{plant.scientific_name}</p>
                                        )}
                                        {plant.indigenous_name && (
                                          <p className="text-sm text-green-700 mb-2">Indigenous name: {plant.indigenous_name}</p>
                                        )}
                                        <div className="flex flex-wrap gap-1">
                                          {plant.plant_parts_used?.map((part, partIdx) => (
                                            <Badge key={partIdx} variant="secondary" className="text-xs">{part}</Badge>
                                          ))}
                                        </div>
                                      </div>
                                      <div>
                                        <p className="text-xs font-medium text-gray-700 mb-1">Healing Properties:</p>
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {plant.healing_properties?.map((prop, propIdx) => (
                                            <Badge key={propIdx} className="bg-blue-100 text-blue-800 text-xs">{prop}</Badge>
                                          ))}
                                        </div>
                                        {plant.active_compounds && (
                                          <p className="text-xs text-gray-600">Active compounds: {plant.active_compounds}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Cultural Significance */}
                          {remedy.cultural_significance && (
                            <div className="mb-4">
                              <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                <Star className="w-4 h-4 mr-2 text-amber-500" />
                                Cultural Significance
                              </h5>
                              <p className="text-sm text-gray-700 bg-amber-50 rounded-lg p-3">{remedy.cultural_significance}</p>
                            </div>
                          )}

                          {/* Preparation Instructions */}
                          {remedy.preparation_instructions && remedy.preparation_instructions.length > 0 && (
                            <div className="mb-4">
                              <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                                Preparation Instructions
                              </h5>
                              <ol className="text-sm text-gray-700 space-y-1 pl-4">
                                {remedy.preparation_instructions.map((instruction, instrIdx) => (
                                  <li key={instrIdx} className="list-decimal">{instruction}</li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {/* Usage Instructions */}
                          {remedy.usage_instructions && remedy.usage_instructions.length > 0 && (
                            <div className="mb-4">
                              <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                <Heart className="w-4 h-4 mr-2 text-red-500" />
                                Usage Instructions
                              </h5>
                              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                                {remedy.usage_instructions.map((usage, usageIdx) => (
                                  <li key={usageIdx} className="list-disc">{usage}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Safety Warnings */}
                          {remedy.safety_warnings && remedy.safety_warnings.length > 0 && (
                            <div className="mb-4">
                              <h5 className="font-semibold text-red-700 mb-2 flex items-center">
                                <AlertTriangle className="w-4 h-4 mr-2 text-red-500" />
                                Safety Warnings
                              </h5>
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <ul className="text-sm text-red-700 space-y-1">
                                  {remedy.safety_warnings.map((warning, warnIdx) => (
                                    <li key={warnIdx} className="flex items-start">
                                      <span className="text-red-500 mr-2">•</span>
                                      {warning}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Modern Validation */}
                          {remedy.modern_validation && (
                            <div>
                              <h5 className="font-semibold text-gray-900 mb-2 flex items-center">
                                <Award className="w-4 h-4 mr-2 text-purple-600" />
                                Modern Scientific Validation
                              </h5>
                              <p className="text-sm text-gray-700 bg-purple-50 rounded-lg p-3">{remedy.modern_validation}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                /* Fallback to signature remedies if detailed remedies aren't loaded */
                selectedRegion.signature_remedies && selectedRegion.signature_remedies.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Signature Healing Remedies</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {selectedRegion.signature_remedies.map((remedy, idx) => (
                        <Card key={idx} className="bg-white">
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-green-900 mb-2">{remedy}</h4>
                            <p className="text-sm text-gray-600">Traditional healing remedy from {selectedRegion.tribe_name}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  };














  const printHealingCard = () => {
    if (!healingResponse) return;
    
    // Create printable version
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Healing Plan Card - ${healingResponse.ailment}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .card { border: 2px solid #8B4513; border-radius: 10px; padding: 20px; margin: 20px 0; }
            h1 { color: #8B4513; text-align: center; }
            h2 { color: #228B22; border-bottom: 1px solid #ccc; }
            .herb { margin: 10px 0; padding: 10px; background: #f9f9f9; }
            .routine { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dotted #ccc; }
            .warning { background: #fff3cd; padding: 10px; border-left: 4px solid #ffc107; margin: 10px 0; }
          </style>
        </head>
        <body>
          <h1>🌿 Personal Healing Plan Card 🌿</h1>
          <div class="card">
            <h2>Ailment: ${healingResponse.ailment || 'Not specified'}</h2>
            <h2>Primary Herbal Protocol</h2>
            ${healingResponse.herbalProtocol && healingResponse.herbalProtocol.primary ? 
              healingResponse.herbalProtocol.primary.map(herb => `
                <div class="herb">
                  <strong>${herb.name || 'Unknown Herb'}</strong> (${herb.tradition || 'Traditional'})<br>
                  <strong>Preparation:</strong> ${herb.preparation || 'Not specified'}<br>
                  <strong>Dosage:</strong> ${herb.dosage || 'Consult practitioner'}<br>
                  <strong>Benefits:</strong> ${herb.benefits || 'Traditional wellness support'}
                </div>
              `).join('') : 
              '<p>No herbal protocol available for this session.</p>'
            }
            
            <h2>Daily Routine</h2>
            ${healingResponse.dailyRoutine && Array.isArray(healingResponse.dailyRoutine) ? 
              healingResponse.dailyRoutine.map(item => `
                <div class="routine">
                  <span>${item.time || 'Time not specified'}</span>
                  <span>${item.activity || 'Activity not specified'}</span>
                  <span>${item.duration || 'Duration varies'}</span>
                </div>
              `).join('') : 
              '<p>No daily routine available for this session.</p>'
            }
            
            <div class="warning">
              <strong>Important:</strong> ${healingResponse.warnings && Array.isArray(healingResponse.warnings) ? 
                healingResponse.warnings.join(' • ') : 
                'Please consult with a healthcare practitioner before using any herbal remedies.'
              }
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };



  return (
    <SubscriptionGate 
      feature="healing_generator"
      title="Healing & Herbal Remedy Generator"
      description="Access unlimited healing wisdom with natural herbal protocols, spiritual cleansing rituals from Indigenous, African, and Caribbean traditions"
    >
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      {/* Hero Banner */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-green-100/80 via-blue-100/60 to-purple-100/80"></div>
          {/* Floating healing elements */}
          <div className="absolute top-20 left-20 w-4 h-4 bg-green-400 rounded-full animate-pulse shadow-lg opacity-60"></div>
          <div className="absolute top-32 right-32 w-3 h-3 bg-blue-400 rounded-full animate-pulse shadow-lg opacity-60"></div>
          <div className="absolute bottom-40 left-1/3 w-5 h-5 bg-purple-400 rounded-full animate-pulse shadow-lg opacity-60"></div>
          <div className="absolute bottom-20 right-20 w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-lg opacity-60"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Healing Grounds
              <br />
              <span className="text-green-700">Your Digital Village Healer</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
              Speak your ailments and receive ancestral wisdom. Natural herbal protocols, 
              spiritual cleansing rituals, and healing guidance from Indigenous, African, and Caribbean traditions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Healing Interface */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation - Always Show */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg p-1 shadow-lg flex flex-col sm:flex-row">
              <button
                onClick={() => setActiveTab('healing')}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-md font-medium transition-all text-sm sm:text-base ${
                  activeTab === 'healing'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 inline" />
                Healing Wisdom
              </button>
              <button
                onClick={() => setActiveTab('tribal-remedies')}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-md font-medium transition-all text-sm sm:text-base ${
                  activeTab === 'tribal-remedies'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <TreePine className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 inline" />
                Tribal Remedies
              </button>
            </div>
          </div>

          {/* Content Based on Active Tab */}
          {activeTab === 'tribal-remedies' ? (
            <div className="space-y-8">
              {/* Tribal Healing Remedies Content - Always Accessible */}
              {renderTribalRemediesContent()}
            </div>
          ) : (
            /* Healing Wisdom Content - Requires Authentication */
            !isAuthenticated ? (
              <Card className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm shadow-lg">
                <CardContent className="p-6 text-center">
                  <Leaf className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Access Sacred Healing Wisdom
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Sign in to access personalized herbal protocols and spiritual guidance from ancestral healing traditions.
                  </p>
                  <AuthModal>
                    <Button className="bg-green-700 hover:bg-green-800 text-white px-6 py-2">
                      Sign In for Healing Guidance
                    </Button>
                  </AuthModal>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Tradition Selector - only show for healing tab when authenticated */}
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                  {healingTraditions.map((tradition) => {
                    const Icon = tradition.icon;
                    return (
                      <button
                        key={tradition.id}
                        onClick={() => setSelectedTradition(tradition.id)}
                        className={`flex items-center px-4 py-2 rounded-full transition-all ${
                          selectedTradition === tradition.id 
                            ? tradition.color + ' shadow-lg scale-105' 
                            : 'bg-white text-gray-700 hover:shadow-md hover:scale-102'
                        }`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {tradition.name}
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Existing Healing Wisdom Content */}
                  {renderHealingWisdomContent()}
                </div>
              </>
            )
          )}
        </div>

        {/* Subscription Registration Modal */}
        <SubscriptionRegistrationModal
          isOpen={showSubscriptionModal}
          onClose={() => {
            setShowSubscriptionModal(false);
            setSelectedTier('seekers');
          }}
          subscriptionTier={selectedTier}
          subscriptionPlan={selectedTier === 'seekers' ? {
            name: '⭐ The Seekers Path',
            monthly_price: 9.99,
            description: 'Access to premium healing content and traditional remedies',
            features: [
              'Full access to Indigenous Healing Remedies database',
              'Traditional plant medicine protocols',
              'Ancestral healing wisdom from 24+ tribes',
              'Sacred plant preparation methods',
              'Complete healing guidance system'
            ]
          } : {
            name: '👑 The Crown Subscription',
            monthly_price: 9.99,
            activation_fee: 129.99,
            description: 'Premium access to all healing content plus exclusive features',
            features: [
              'Everything in Seekers Path',
              'Priority access to new healing content',
              'Advanced tribal healing protocols',
              'Personal healing consultation access',
              'Exclusive ceremonial healing practices'
            ]
          }}
          onSubscriptionSuccess={() => {
            setShowSubscriptionModal(false);
            setSelectedTier('seekers');
          }}
        />
      </section>
      </div>
    </SubscriptionGate>
  );
};

export default HealingGrounds;